// Basemap catalog — single source of truth for the "Mapa Base" tool.
// Thumbnails prefer Esri's dedicated endpoints (raster: /info/thumbnail, vector: styles API /thumbnail).
// ChangeBasemap.vue uses @error to fall back to a placeholder when a thumbnail 404s.

const ESRI_KEY = '';
const ESRI_RASTER = 'https://server.arcgisonline.com/ArcGIS/rest/services';
const ESRI_STYLE = 'https://basemapstyles-api.arcgis.com/arcgis/rest/services/styles/v2/styles';
// ─── Placeholder: self-contained SVG data URI, zero external dependencies ───
const PLACEHOLDER_THUMB =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' " +
  "width='256' height='160'%3E%3Crect width='256' height='160' fill='%23d0d0d0'/%3E" +
  "%3Ctext x='128' y='88' text-anchor='middle' font-family='sans-serif' " +
  "font-size='13' fill='%23888'%3EMapa%3C/text%3E%3C/svg%3E";
const styleUrl = (enumName) => `${ESRI_STYLE}/${enumName}?type=style&token=${ESRI_KEY}`;
const rasterUrl = (svc) =>
  `${ESRI_RASTER}/${svc}/MapServer/tile/{z}/{y}/{x}`;

// Builds a full ArcGIS Online thumbnail URL given an item ID and its exact filename.
// Filename must be verified from: /sharing/rest/content/items/{id}?f=json → "thumbnail" field.
const agolThumb = (id, filename) =>
  `https://www.arcgis.com/sharing/rest/content/items/${id}/info/thumbnail/${filename}`;

const BASEMAP_ITEMS = {
  World_Imagery: "50c23e4987a44de4ab163e1baeab4a46",
  World_Street_Map: "de26a3cf4cc9451298ea173c4b324736",
  World_Topo_Map: "6e03e4c6c4c34c1bb3f90f0bb5c8d43c",
  World_Terrain_Base: "1b243539f4514b6ba35e7d995890db1d",
  NatGeo_World_Map: "f33a34de3a294590ab48f246e99958c9",

  // Canvas styles
  "Canvas/World_Light_Gray_Base": "ed712cb1db3e4bae9e85329040fb9a49",
  "Canvas/World_Dark_Gray_Base": "8d91bd39e873417ea21673e0fee87604",

  // Oceans
  "Ocean/World_Ocean_Base": "b1dca7ef7b61466785901c41aed89ba5"
};

/* Bug fix 20260429 - HTTP 400 on basemap thumbnails
   The code was using the wrong filename for the thumbnails.
   Under the thumbnail json file, "thumbnail" field needs to be checked for the actual file name
   ie. "de26a3cf4cc9451298ea173c4b324736?=fjson" has {..."thumbnail":"thumbnail/thumbnail1561650886307.jpeg",...}

   If filenames change, search the "thumbnail" field under the following URL:
   https://www.arcgis.com/sharing/rest/content/items/{ANY_ID_FROM_BASEMAP_ITEMS}?f=json

*/

const vectorThumb = (enumName) => `${ESRI_STYLE}/${enumName}/thumbnail?token=${ESRI_KEY}`;

export const baseMapCatalog = [
  //Se tradujeron nombres de mapas al español segun observacion de SENAPRED 06/2026 ("Sería bueno tener los nombres de los mapas base en español (los que se puedan, por ejemplo OpenStreetMap no.)")
  {
    id: 'osm',
    name: 'Por Defecto',
    //name: 'OpenStreetMap',
    type: 'osm',
    url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    thumbnail: 'https://tile.openstreetmap.org/3/4/2.png',
  },
  {
    id: 'maptiles',
    name: 'Mapa Interno',
    type: 'xyz',
    url: process.env.VUE_APP_MAPTILES,
    thumbnail: require('@/assets/img/folded-paper-map-of-world-with-color-point-marker-illustration-vector (1).jpg'),
  },
  {
    id: 'imagery',
    name: 'Satelital',
    type: 'xyz',
    url: rasterUrl('World_Imagery'),
    // id confirmed: 50c23e4987a44de4ab163e1baeab4a46 → thumbnail: "thumbnail/ago_downloaded.png"
    thumbnail: agolThumb('50c23e4987a44de4ab163e1baeab4a46', 'ago_downloaded.png'),
  },
  {
    id: 'imagery-hybrid',
    name: 'Satelital Híbrido',
    type: 'xyz',
    url: rasterUrl('World_Imagery'),
    thumbnail: agolThumb('50c23e4987a44de4ab163e1baeab4a46', 'ago_downloaded.png'),
    overlayUrl: rasterUrl('Reference/World_Boundaries_and_Places'),
  },
  {
    id: 'streets',
    name: 'Calles',
    type: 'xyz',
    url: rasterUrl('World_Street_Map'),
    // id confirmed: de26a3cf4cc9451298ea173c4b324736 → thumbnail: "thumbnail/thumbnail1561650886307.jpeg"
    thumbnail: agolThumb('de26a3cf4cc9451298ea173c4b324736', 'thumbnail1561650886307.jpeg'),
  },

  {
    id: 'topographic',
    name: 'Topográfico',
    type: 'xyz',
    url: rasterUrl('World_Topo_Map'),
    //Se agrega thumbnail en vez de placeholder segun observacion de SENAPRED 06/2026 ("Hay mapas base sin imagen.")
    thumbnail: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/3/2/4',
  },
  {
    id: 'terrain-labels',
    name: 'De Terreno con Etiquetas',
    type: 'xyz',
    url: rasterUrl('World_Terrain_Base'),
    // id confirmed: 1b243539f4514b6ba35e7d995890db1d → thumbnail: "thumbnail/thumbnail1718052727979.png"
    thumbnail: agolThumb('1b243539f4514b6ba35e7d995890db1d', 'thumbnail1718052727979.png'),
    overlayUrl: rasterUrl('Reference/World_Reference_Overlay'),
  },
  {
    id: 'light-gray',
    name: 'Lienzo Gris Claro',
    type: 'xyz',
    url: rasterUrl('Canvas/World_Light_Gray_Base'),
    // id confirmed: ed712cb1db3e4bae9e85329040fb9a49 → thumbnail: "thumbnail/thumbnail1574667178807.jpeg"
    thumbnail: agolThumb('ed712cb1db3e4bae9e85329040fb9a49', 'thumbnail1574667178807.jpeg'),
  },
  {
    id: 'dark-gray',
    name: 'Lienzo Gris Oscuro',
    type: 'xyz',
    url: rasterUrl('Canvas/World_Dark_Gray_Base'),
    // id confirmed: 8d91bd39e873417ea21673e0fee87604 → thumbnail: "thumbnail/thumbnail1580490916023.jpeg"
    thumbnail: agolThumb('8d91bd39e873417ea21673e0fee87604', 'thumbnail1580490916023.jpeg'),
  },
  {
    id: 'oceans',
    name: 'Océanos',
    type: 'xyz',
    url: rasterUrl('Ocean/World_Ocean_Base'),
    // ✗ item 6348e67824504fc9a62976434bf0d8d5 returns HTTP 400 — org-restricted.
    // id confirmed: 1e126e7520f9466c9ca28b8f28b5e500 → thumbnail: "thumbnail/thumbnail1574706730697.jpeg"
    thumbnail: agolThumb('1e126e7520f9466c9ca28b8f28b5e500', 'thumbnail1574706730697.jpeg'),
  },
  {
    id: 'natgeo',
    name: 'Nacional Geográfico',
    type: 'xyz',
    url: rasterUrl('NatGeo_World_Map'),
    // id confirmed: f33a34de3a294590ab48f246e99958c9 → thumbnail: "thumbnail/NatGeo_WM_600x400_thumb.jpg"
    thumbnail: agolThumb('f33a34de3a294590ab48f246e99958c9', 'NatGeo_WM_600x400_thumb.jpg'),
  },
];

export const DEFAULT_BASEMAP_ID = 'osm';
export const PLACEHOLDER_THUMB_URL = PLACEHOLDER_THUMB;
